// A plane of the dashboard grid (DESIGN D6; DESIGN-R2 B4, B7, B9, B11, B12, B14, B15): the DATAFLOW plane (bands by
// layer, columns by source system, the rail) and the CONTROL PLANE (Fabric / ADF / Portal x Repo / Deploy / Deployed /
// Verified, then the Watchers row). A CSS grid with a left gutter of band labels; each cell holds its tiles in model
// order (flex-wrap); a tile with 2+ columns spans them (and grows to fill them when a column would otherwise be empty),
// a tile with 2+ bands fills its whole spanned height. One absolutely positioned SVG overlay draws the connectors,
// computed in a layout effect from the measured boxes of the [data-asset-id] tiles and of every label a wire must not
// cross, relative to the grid container, and recomputed on ResizeObserver (the container and every tile), window
// resize, any <details> toggle inside the grid and fonts-ready. A JDBC wire's label sits beside its last leg below the
// rail, clear of every other wire and of the rail bar (DESIGN-R3 C13). Each plane grid sits in its own overflow-x:auto
// container with a visible scrollbar and, when it overflows, a visible "scroll for N more columns" cue - the page body
// never scrolls sideways.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { HealthGridLink, HealthGridRail } from "./types";
import type { GridCtx } from "./ctx";
import { AssetTile } from "./AssetTile";
import {
  LABEL_BASE,
  RAIL_LABEL_FREE_SIDES,
  RAIL_LABEL_PAD,
  lanesFromHeads,
  placeWireLabels,
  routeLinks,
  type Guide,
  type PlaneLayout,
  type Rect,
  type RouteEnv,
  type RoutedPath,
  type WireLabel
} from "./grid";
import { plural } from "./present";

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

interface Geometry {
  w: number;
  h: number;
  paths: RoutedPath[];
  /** the JDBC wires' labels (DESIGN-R3 C13) */
  labels: WireLabel[];
}

/** a connector's identity: two links may join the same tiles as flow and as JDBC, or directly and via the rail */
export const wireKey = (p: { from: string; to: string; kind: string; via: string | null }) => `${p.from}>${p.to}:${p.kind}:${p.via ?? ""}`;

const toD = (pts: [number, number][]) => pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x} ${y}`).join(" ");
const sig = (g: Geometry) =>
  `${g.w}x${g.h}|${g.paths.map(p => `${wireKey(p)}:${p.points.join(";")}`).join("|")}|${g.labels.map(l => `${l.path}@${l.box.x},${l.box.y}`).join("|")}`;

/** the arrowhead: 6px, in the SVG's own units (never scaled by the stroke width), so it always fits the last leg */
export const ARROW_PX = 6;

// R5: the overlay is sized to its grid by CSS (health.css .ph-wires: width / height 100%), so it always sits on the grid; until
// the wires are recomputed after a resize or a toggle its drawing stays anchored top-left (never re-centred)
const OVERLAY_ASPECT = "xMinYMin meet";

function Wires({ plane, geo }: { plane: string; geo: Geometry | null }) {
  if (geo === null)
    return <svg className="ph-wires" aria-hidden="true" data-testid={`wires-${plane}`} width={0} height={0} preserveAspectRatio={OVERLAY_ASPECT} />;
  const arrow = (kind: string) => `ph-arrow-${plane}-${kind}`;
  return (
    <svg
      className="ph-wires"
      aria-hidden="true"
      data-testid={`wires-${plane}`}
      data-paths={geo.paths.length}
      width={geo.w}
      height={geo.h}
      viewBox={`0 0 ${geo.w} ${geo.h}`}
      preserveAspectRatio={OVERLAY_ASPECT}
    >
      <defs>
        {["flow", "jdbc"].map(k => (
          <marker
            key={k}
            id={arrow(k)}
            className={`ph-arrow ph-arrow-${k}`}
            viewBox="0 0 8 8"
            refX="8"
            refY="4"
            markerUnits="userSpaceOnUse"
            markerWidth={ARROW_PX}
            markerHeight={ARROW_PX}
            orient="auto"
          >
            <path d="M0,0 L8,4 L0,8 z" />
          </marker>
        ))}
      </defs>
      {/* flow wires first, JDBC after: a JDBC dash never hides a flow wire (they never share a stroke anyway) */}
      {[...geo.paths.filter(p => p.kind !== "jdbc"), ...geo.paths.filter(p => p.kind === "jdbc")].map(p => (
        <path
          key={wireKey(p)}
          className={`ph-wire ph-wire-${p.kind}${p.clear ? "" : " ph-wire-blocked"}`}
          d={toD(p.points)}
          markerEnd={`url(#${arrow(p.kind)})`}
          data-wire=""
          data-from={p.from}
          data-to={p.to}
          data-kind={p.kind}
          data-via={p.via ?? ""}
          data-clear={p.clear ? "true" : "false"}
          data-relax={p.relax ?? 0}
          data-points={JSON.stringify(p.points)}
        />
      ))}
      {geo.labels.map(lb => {
        const p = geo.paths[lb.path];
        // R4 D5: one label per last leg - `data-also` names the other JDBC wires sharing it; the text is anchored at the
        // leg side of its box, so it sits LABEL_GAP px beside the leg on either side
        const also = (lb.also ?? []).map(k => geo.paths[k]).filter(Boolean).map(q => `${q.from}>${q.to}`);
        return p ? (
          <text
            key={`l-${wireKey(p)}`}
            className="ph-wire-label"
            x={lb.side === "left" ? lb.box.x + lb.box.w : lb.box.x}
            y={lb.box.y + LABEL_BASE}
            textAnchor={lb.side === "left" ? "end" : "start"}
            data-from={p.from}
            data-to={p.to}
            data-kind={p.kind}
            data-side={lb.side}
            data-also={also.length > 0 ? also.join(" ") : undefined}
            data-clear={lb.clear ? "true" : "false"}
          >
            JDBC
          </text>
        ) : null;
      })}
    </svg>
  );
}

interface Cue {
  right: string[];
  left: string[];
  /** the visible part of the plane, as fractions of its full width (the page's own scroll indicator) */
  at: number;
  frac: number;
}

/** connectors are recomputed this long after the last change (a resize drag, a toggle) - the first draw is immediate */
const SETTLE_MS = 60;

export function GridPlane({
  plane,
  title,
  hint,
  layout,
  links,
  rail = null,
  ctx,
  minWidthRem,
  children
}: {
  plane: "dataflow" | "control";
  title: string;
  hint: string;
  layout: PlaneLayout;
  links: HealthGridLink[];
  rail?: HealthGridRail | null;
  ctx: GridCtx;
  minWidthRem: number;
  children?: ReactNode;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState<Geometry | null>(null);
  const [cue, setCue] = useState<Cue | null>(null);
  const ids = layout.cells.flatMap(c => c.assets.map(a => a.id)).join(",");

  const measureCue = useCallback(() => {
    const sc = scrollRef.current;
    const g = gridRef.current;
    if (!sc || !g) return;
    let next: Cue | null = null;
    if (sc.scrollWidth > sc.clientWidth + 1) {
      const view = sc.getBoundingClientRect();
      const left0 = view.left + sc.clientLeft;
      const right0 = left0 + sc.clientWidth;
      next = { right: [], left: [], at: Math.round((sc.scrollLeft / sc.scrollWidth) * 1000) / 1000, frac: Math.round((sc.clientWidth / sc.scrollWidth) * 1000) / 1000 };
      g.querySelectorAll<HTMLElement>("[data-colhead-first]").forEach(el => {
        const r = el.getBoundingClientRect();
        const name = (el.textContent ?? "").trim() || "-";
        if (r.right > right0 + 1) next!.right.push(r.left < right0 - 1 ? `${name} (part)` : name);
        else if (r.left < left0 - 1) next!.left.push(r.right > left0 + 1 ? `${name} (part)` : name);
      });
    }
    setCue(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, []);

  const recompute = useCallback(() => {
    const g = gridRef.current;
    if (!g) return;
    const box = g.getBoundingClientRect();
    const rel = (r: DOMRect): Rect => ({ x: r.left - box.left, y: r.top - box.top, w: r.width, h: r.height });
    const rects = new Map<string, Rect>();
    g.querySelectorAll<HTMLElement>("[data-asset-id]").forEach(el => {
      const id = el.dataset.assetId;
      if (id) rects.set(id, rel(el.getBoundingClientRect()));
    });
    // every label a wire must not cross (B14): band labels, column / segment chips, the rail's own labels
    const labels: NonNullable<RouteEnv["labels"]> = [];
    g.querySelectorAll<HTMLElement>(".ph-band-label, .ph-colhead-text, .ph-rail-label, .ph-rail-jdbc").forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const railLabel = el.classList.contains("ph-rail-label") || el.classList.contains("ph-rail-jdbc");
      // R4 D4: the rail label keeps two sides free of wires (never framed)
      const free = el.classList.contains("ph-rail-label") ? { free: RAIL_LABEL_FREE_SIDES } : {};
      if (r.width > 0 && r.height > 0) labels.push({ id: `label:${i}`, rect: rel(r), ...(railLabel ? { pad: RAIL_LABEL_PAD } : {}), ...free });
    });
    const railEl = g.querySelector<HTMLElement>('[data-testid="rail"]');
    const barEl = g.querySelector<HTMLElement>("[data-rail-line]");
    let railEnv: RouteEnv["rail"] = null;
    if (railEl && barEl) {
      const rb = rel(railEl.getBoundingClientRect());
      const bar = rel(barEl.getBoundingClientRect());
      railEnv = { box: rb, flowY: bar.y - 4, jdbcY: bar.y + bar.h + 4 };
    }
    const heads = Array.from(g.querySelectorAll<HTMLElement>("[data-colhead-first]")).map(el => rel(el.getBoundingClientRect()));
    // R5 E2: the lanes sit beside each column edge (never on it), and every dashed divider (a 1px dashed border-left: the
    // line's centre) is kept GUIDE_SIDE px clear of any wire running along it
    const lanes = lanesFromHeads(heads);
    const guides: Guide[] = Array.from(g.querySelectorAll<HTMLElement>(".ph-colguide"))
      .map(el => rel(el.getBoundingClientRect()))
      .filter(r => r.h > 0)
      .map(r => ({ x: r.x + 0.5, y0: r.y, y1: r.y + r.h }));
    const env: RouteEnv = { width: g.clientWidth, height: g.clientHeight, labels, rail: railEnv, lanes, guides };
    const paths = routeLinks(links, rects, env);
    const next: Geometry = { w: g.clientWidth, h: g.clientHeight, paths, labels: placeWireLabels(paths, rects, env) };
    setGeo(prev => (prev !== null && sig(prev) === sig(next) ? prev : next));
  }, [links]);

  // the cue is measured AFTER the overlay has its new size (an overlay still at the old size would count as overflow)
  useIsoLayoutEffect(() => {
    measureCue();
  }, [geo, measureCue]);

  useIsoLayoutEffect(() => {
    recompute();
    const g = gridRef.current;
    const sc = scrollRef.current;
    if (!g) return;
    let raf = 0;
    let timer = 0;
    const schedule = () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
      timer = window.setTimeout(() => {
        raf = requestAnimationFrame(recompute);
      }, SETTLE_MS);
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (ro) {
      ro.observe(g);
      if (sc) ro.observe(sc);
      g.querySelectorAll("[data-asset-id]").forEach(el => ro.observe(el));
    }
    g.addEventListener("toggle", schedule, true);   // <details> toggles do not bubble; capture sees them
    window.addEventListener("resize", schedule);
    sc?.addEventListener("scroll", measureCue, { passive: true });
    document.fonts?.ready.then(schedule).catch(() => undefined);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro?.disconnect();
      g.removeEventListener("toggle", schedule, true);
      window.removeEventListener("resize", schedule);
      sc?.removeEventListener("scroll", measureCue);
    };
  }, [recompute, measureCue, ids]);

  const hasJdbc = rail !== null && rail.to.some(t => t.kind === "jdbc");
  const cueText =
    cue === null
      ? ""
      : [
          cue.right.length > 0 ? `scroll right for ${plural(cue.right.length, "more column")}: ${cue.right.join(", ")}` : "",
          cue.left.length > 0 ? `scroll left for ${plural(cue.left.length, "column")}: ${cue.left.join(", ")}` : ""
        ]
          .filter(Boolean)
          .join(" · ") || "scroll sideways for the rest of the grid";
  return (
    <section className={`ph-plane ph-plane-${plane}`} aria-labelledby={`ph-plane-${plane}-title`} data-testid={`plane-${plane}`}>
      <div className="ph-section-head">
        <h2 id={`ph-plane-${plane}-title`}>{title}</h2>
        <span className="ph-hint">{hint}</span>
        {plane === "dataflow" ? (
          <span className="ph-wire-legend ph-hint" aria-hidden="true">
            <svg width="34" height="10">
              <path className="ph-wire ph-wire-flow" d="M1 5 L33 5" />
            </svg>{" "}
            flow{" "}
            {hasJdbc ? (
              <>
                <svg width="34" height="10">
                  <path className="ph-wire ph-wire-jdbc" d="M1 5 L33 5" />
                </svg>{" "}
                {rail?.jdbc_label}
              </>
            ) : null}
          </span>
        ) : null}
      </div>
      <p className="ph-scroll-cue" data-testid={`scroll-cue-${plane}`} hidden={cueText === ""} aria-live="polite">
        <span aria-hidden="true">{cue && cue.right.length > 0 ? "→ " : "← "}</span>
        {cueText}
      </p>
      <div
        className="ph-plane-scroll"
        data-testid={`plane-scroll-${plane}`}
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-label={`${title} grid - scrolls sideways when it is wider than the screen`}
      >
        <div
          ref={gridRef}
          className="ph-grid"
          data-grid={plane}
          style={{
            gridTemplateColumns: `var(--ph-gutter) repeat(${layout.columns}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${layout.rowCount}, auto)`,
            minWidth: `${minWidthRem}rem`
          }}
        >
          {layout.bands.map(b => (
            <div
              key={`bg-${b.id}`}
              className={`ph-band-bg${b.index % 2 === 1 ? " ph-band-bg-alt" : ""}`}
              style={{ gridColumn: "1 / -1", gridRow: `${b.row} / span ${b.rowSpan}` }}
              aria-hidden="true"
            />
          ))}
          {layout.guides.map(gd => (
            <div
              key={`guide-${gd.col}-${gd.rowStart}`}
              className="ph-colguide"
              style={{ gridColumn: gd.col, gridRow: `${gd.rowStart} / ${gd.rowEnd}` }}
              data-guide={`${gd.col}:${gd.rowStart}-${gd.rowEnd}`}
              aria-hidden="true"
            />
          ))}
          {layout.bands.map(b => (
            <div key={`label-${b.id}`} className="ph-band-label" style={{ gridColumn: 1, gridRow: `${b.row} / span ${b.rowSpan}` }} data-band={b.id}>
              {b.label}
            </div>
          ))}
          {layout.headers.map(h =>
            h.cells.map(c => (
              <div
                key={`h-${h.row}-${c.col}`}
                className={`ph-colhead${h.first ? " ph-colhead-top" : " ph-seghead"}`}
                style={{ gridColumn: c.col, gridRow: h.row }}
                data-colhead={h.first ? "top" : "segment"}
                data-colhead-first={h.first ? "" : undefined}
                data-before-band={h.beforeBand}
              >
                <span className="ph-colhead-text">
                  {c.label || "-"}
                  {c.continued ? <span className="ph-continued"> (continued)</span> : null}
                </span>
              </div>
            ))
          )}
          {layout.rail && rail ? (
            <>
              <div className="ph-band-label ph-rail-gutter" style={{ gridColumn: 1, gridRow: layout.rail.row }}>
                rail
              </div>
              <div
                className="ph-rail"
                style={{ gridColumn: `${layout.rail.col} / span ${layout.rail.colSpan}`, gridRow: layout.rail.row }}
                data-testid="rail"
              >
                <span className="ph-rail-label">{rail.label}</span>
                {hasJdbc ? <span className="ph-rail-jdbc">{rail.jdbc_label}</span> : null}
                <div className="ph-rail-line" data-rail-line="" />
              </div>
            </>
          ) : null}
          {layout.cells.map(c => (
            <div
              key={c.key}
              className="ph-cell"
              style={{ gridColumn: `${c.col} / span ${c.colSpan}`, gridRow: `${c.row} / span ${c.rowSpan}` }}
              data-cell={c.key}
              data-col-span={c.colSpan}
              data-fill={[c.fill.tall ? "tall" : "", c.fill.wide ? "wide" : ""].filter(Boolean).join(" ") || undefined}
            >
              {c.assets.map(a => (
                <AssetTile key={a.id} a={a} />
              ))}
            </div>
          ))}
          <Wires plane={plane} geo={geo} />
        </div>
      </div>
      <div
        className="ph-scroll-track"
        data-testid={`scroll-track-${plane}`}
        hidden={cue === null}
        aria-hidden="true"
        onClick={e => {
          const sc = scrollRef.current;
          if (!sc) return;
          const r = e.currentTarget.getBoundingClientRect();
          sc.scrollLeft = ((e.clientX - r.left) / Math.max(1, r.width)) * sc.scrollWidth - sc.clientWidth / 2;
        }}
      >
        <div className="ph-scroll-thumb" style={{ left: `${(cue?.at ?? 0) * 100}%`, width: `${(cue?.frac ?? 1) * 100}%` }} />
      </div>
      {children}
      {ctx.data.grid.assets.length === 0 ? <p className="ph-reason">UNKNOWN - the answer's grid holds no asset</p> : null}
    </section>
  );
}
